import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";

const FORMATS = [
  { id: "csv", label: "CSV" },
  { id: "xlsx", label: "EXCEL" },
  { id: "pdf", label: "PDF" },
];

export default function Exports() {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ project_id: "", user_id: "", start_date: "", end_date: "" });

  useEffect(() => {
    Promise.all([api.get("/projects"), api.get("/users")]).then(([p, u]) => {
      setProjects(p.data); setEmployees(u.data);
    });
  }, []);

  const exportFile = async (endpoint, fmt) => {
    const params = new URLSearchParams({ fmt });
    Object.entries(filter).forEach(([k, v]) => { if (v) params.append(k, v); });
    const r = await api.get(`/exports/${endpoint}?${params.toString()}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const ext = fmt === "xlsx" ? "xlsx" : fmt === "pdf" ? "pdf" : "csv";
    const a = document.createElement("a"); a.href = url; a.download = `${endpoint}_${Date.now()}.${ext}`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="exports-page">
      <div>
        <div className="label-eyebrow">REPORTING</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Exports</h1>
        <p className="text-sm text-[#888] mt-2 max-w-2xl">Download timesheets, daily reports, leave records and upload logs. Apply filters below for narrowed exports.</p>
      </div>

      {/* Filters */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
        <div className="label-eyebrow mb-4">FILTERS</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="label-eyebrow">PROJECT</Label>
            <Select value={filter.project_id || "all"} onValueChange={(v) => setFilter({ ...filter, project_id: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                <SelectItem value="all">Any</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">EMPLOYEE</Label>
            <Select value={filter.user_id || "all"} onValueChange={(v) => setFilter({ ...filter, user_id: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                <SelectItem value="all">Any</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-eyebrow">FROM</Label><div className="mt-2"><DatePicker value={filter.start_date} onChange={(v) => setFilter({ ...filter, start_date: v })} /></div></div>
          <div><Label className="label-eyebrow">TO</Label><div className="mt-2"><DatePicker value={filter.end_date} onChange={(v) => setFilter({ ...filter, end_date: v })} /></div></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { id: "time", label: "Time Tracking Report", desc: "All clock-in / clock-out entries" },
          { id: "daily-reports", label: "Daily Reports", desc: "Field reports submitted by employees" },
          { id: "leaves", label: "Leave Report", desc: "Leave requests, decisions & balances" },
          { id: "uploads", label: "Upload Log", desc: "All files uploaded to the portal" },
        ].map((r) => (
          <Card key={r.id} className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
            <div className="font-heading text-lg font-bold uppercase tracking-tight">{r.label}</div>
            <div className="text-sm text-[#888] mt-1">{r.desc}</div>
            <div className="flex gap-2 mt-4">
              {FORMATS.map((f) => (
                <Button
                  key={f.id}
                  variant="ghost"
                  onClick={() => exportFile(r.id, f.id)}
                  data-testid={`export-${r.id}-${f.id}`}
                  className="rounded-none border border-[#222] font-mono text-xs hover:bg-[#100755] hover:border-[#3b2bd0]"
                >
                  <Download className="h-3 w-3 mr-2" /> {f.label}
                </Button>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Project-specific exports */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
        <div className="label-eyebrow mb-4">PER-PROJECT EXPORT</div>
        <ul className="divide-y divide-[#1c1c1c]">
          {projects.map((p) => (
            <li key={p.id} className="py-3 flex items-center justify-between">
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="font-mono text-xs text-[#666]">{p.client || "—"}</div>
              </div>
              <div className="flex gap-2">
                {FORMATS.map((f) => (
                  <Button
                    key={f.id} variant="ghost"
                    onClick={() => exportFile(`project/${p.id}`, f.id)}
                    className="rounded-none border border-[#222] font-mono text-xs hover:bg-[#100755] hover:border-[#3b2bd0]"
                  >
                    {f.label}
                  </Button>
                ))}
              </div>
            </li>
          ))}
          {projects.length === 0 && <li className="py-6 text-center text-[#666] font-mono text-sm">No projects yet</li>}
        </ul>
      </Card>
    </div>
  );
}
